/**
 * AUTHENTICATION CONTEXT
 * Provides global auth state across the entire app
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiClient } from '../lib/api'

interface User {
  id: number
  email: string
  name: string
  type: 'Admin' | 'Standard'
}

interface AuthContextType {
  user: User | null
  token: string | null
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<any>
  verifyOTP: (email: string, otp: string) => Promise<any>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)

  // Check for existing user session on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user')
    const storedToken = localStorage.getItem('token')

    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser))
        setToken(storedToken)
      } catch (err) {
        console.error('Failed to parse stored user:', err)
        localStorage.removeItem('user')
        localStorage.removeItem('token')
      }
    }
    setIsLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    console.log('AuthContext.login called with:', email)
    setError(null)

    try {
      console.log('Making API call to /auth/login')
      const response = await apiClient.post('/auth/login', { email, password })
      console.log('Login API response:', response)

      if (!response.success) {
        console.log('Login response not successful:', response)
        throw new Error(response.message || 'Login failed')
      }

      console.log('Login successful, returning response')
      return response
    } catch (err: any) {
      console.error('AuthContext.login error:', err)
      const errorMessage = err.message || 'Login failed. Please try again.'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const verifyOTP = async (email: string, otp: string) => {
    console.log('verifyOTP called with:', email, otp)
    setIsLoading(true)
    setError(null)

    try {
      console.log('Making API call to /auth/verify-otp')
      const response = await apiClient.post('/auth/verify-otp', { email, otp })
      console.log('API response received:', response)

      if (!response.success) {
        console.log('Response not successful:', response)
        throw new Error(response.message || 'OTP verification failed')
      }

      console.log('OTP verified, setting user:', response.user)

      // Update user state and store in localStorage
      setUser(response.user)
      setToken(response.token)
      localStorage.setItem('user', JSON.stringify(response.user))
      localStorage.setItem('token', response.token)

      console.log('User state updated, user is now:', response.user)

      return response
    } catch (err: any) {
      console.error('verifyOTP error:', err)
      const errorMessage = err.message || 'OTP verification failed. Please try again.'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    setIsLoading(true)

    try {
      await apiClient.post('/auth/logout', {})
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      setUser(null)
      setToken(null)
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      setIsLoading(false)
    }
  }

  return (
    <AuthContext.Provider value={{ user, token, isLoading, error, login, verifyOTP, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
